import React from 'react';
import { InteractionInfo, OverlayComponentProps } from '$/components/Interactions/InteractionInfo';
import { OverlayInteractionMode } from '$/components/Interactions/types';
import { css } from '@emotion/react';
import { CheckIcon, CloseIcon } from '$/images/Icons';
import {
  staticWebinarDimensions,
  withWebinarPlayerContext,
} from '$/components/WebinarPlayer/context';
import { PlayerMode } from '$/components/WebinarPlayer/types';
import {
  useConversionNotificationsQuery,
  ConversionNotificationFragment,
  useFakeConversionNotificationsLazyQuery,
  SocketNotification,
} from '$/GraphQL/schema/schema';
import Cookies from 'js-cookie';
import MessageEvent from '$/components/Socket/MessageEvent';
import { getLocaleForLang } from '$/shared/frontend/utils/i18n';
import isMobile from '$/utils/isMobile';
import config from '$/config';
import tw from 'twin.macro';
import { Input } from 'semantic-ui-react';
import { readableColorOnBackground } from '$/shared/frontend/utils/tinyColor';
import { AnimatePresence, motion } from 'framer-motion';
import { usePropertyFilledLink } from '$/components/ui/EditableInput/DangerouslySetInnerHTML';
import { useScaledRoomContext } from '$/components/WebinarPlayer/Rooms/RoomParts';
import { useHasChangedShallow } from '$/hooks/useHasChanged';
import { logger } from '$/utils/logger';
import { useWatch } from 'react-hook-form';
import Form from '$/components/ui/Form';
import EDate from '$/shared/frontend/utils/EDate';

const FromNow: React.FC<{ dateString: string; language: string }> = ({ dateString, language }) => {
  const [text, setText] = React.useState('');

  React.useEffect(() => {
    (async function () {
      const dateText = await new EDate(dateString).formatDistanceToNowAsync(
        getLocaleForLang(language)
      );
      setText(dateText);
    })();
  }, [dateString, language]);

  if (!text) return null;
  return <span className='from-date'>{text}</span>;
};

const ConversionNotificationOverlayItem: React.FC<{
  conversion?: ConversionNotificationFragment;
  interaction: InteractionInfo;
  onClose: () => void;
  mode: OverlayInteractionMode;
  isInSettings?: boolean;
}> = withWebinarPlayerContext(
  ['mode', 'ewebinar', 'interactionInEdit', 'interactionInEditModalId'],
  'ConversionItem'
)(({ interaction, onClose, conversion, mode: overlayMode, context, isInSettings }) => {
  const { mode, ewebinar, interactionInEdit, interactionInEditModalId } = context;

  const [isEmptyImage, setEmptyImage] = React.useState(!Boolean(conversion?.imageUrl));
  const [isImageReady, setImageReady] = React.useState(false);
  const descriptionInput = useWatch({ name: 'details.description' });
  const linkWatching = useWatch({ name: 'details.link' });
  const displayLocation = isMobile ? 'top' : interaction.displayLocation();
  const hideAnimation = displayLocation === 'top' ? 'cn.hide.up' : 'cn.hide.down';

  React.useEffect(() => {
    // Load image before we show the card
    if (!conversion?.imageUrl) {
      return;
    }

    setEmptyImage(false);

    const img = new Image();
    img.onload = (): void => {
      setImageReady(true);
    };
    img.onerror = (): void => {
      setEmptyImage(true);
    };

    img.src = conversion.imageUrl;
  }, [conversion?.imageUrl]);

  const { scale } = useScaledRoomContext();
  const detailLink = usePropertyFilledLink(interaction.details?.link);
  const settings = interaction?.details?.overlaySettings;

  if (!conversion || !settings || (!isEmptyImage && !isImageReady)) {
    return null;
  }

  // DD: Why do we need interactionInEditModalId??  Isn't interactionInEdit enough?
  const inCard = overlayMode === OverlayInteractionMode.InCard;
  const isEditable =
    (interactionInEdit?.id === interaction.id || interactionInEditModalId === interaction.id) &&
    inCard;

  return (
    <motion.div
      initial={hideAnimation}
      animate={'cn.show'}
      transition={{ type: 'spring', duration: 0.25 }}
      variants={{
        'cn.show': { y: '0vh', x: '-50%', opacity: 1, scale },
        'cn.hide.up': { y: '-100vh', x: '-50%', opacity: 0, scale },
        'cn.hide.down': { y: '100vh', x: '-50%', opacity: 0, scale },
      }}
      exit={hideAnimation}
      id='ConversionNotification'
      className={`
        flex flex-row items-center bg-white mx-auto scale-overlay
        ${(interaction.details?.link || linkWatching) && 'cursor-pointer pointer-events-auto'}
        ${isEditable && 'is-editable'}
        ${displayLocation === 'top' ? 'on-top' : 'on-bottom'}
      `}
      onClick={(): void => {
        if (linkWatching && mode == PlayerMode.edit && !inCard) {
          window.open(linkWatching, '_blank');
          return;
        }
        if (!interaction.details?.link || interaction.details?.link === '' || inCard) return;

        window.open(detailLink, '_blank');
      }}
      css={css`
        position: ${isInSettings ? 'relative' : 'absolute'};
        pointer-events: all;
        overflow: hidden;
        filter: drop-shadow(0px 0px 1px #00000030);
        border-radius: ${settings.borderRadius ?? 10}px;
        border: 1px solid rgba(216, 217, 226, 0.5);
        left: 50%;
        width: 22rem;
        height: 6rem;

        &.on-top {
          top: 1%;
          transform-origin: center top;
        }
        &.on-bottom {
          bottom: ${isInSettings
            ? 0
            : staticWebinarDimensions.videoControlsArea.bottomBar.height}px;
          transform-origin: center bottom;
        }

        &.is-editable {
          transform: none !important;
          position: relative !important;
          left: 0 !important;
          .notification-title {
            margin-top: 4px;
          }
          .notification-description {
            overflow: visible;
            margin: 4px -6px 0 -6px;
            textarea {
              height: auto;
              max-height: 30px;
              transition: background;
              padding: 0.25rem 0.25rem !important;
              ${tw`duration-100 `}
              resize: none;
              overflow: hidden;
              border-width: 2px;
              border-style: dashed;
              &:focus {
                background: rgba(0, 0, 0, 0.1) !important;
              }
              &::placeholder {
                color: #99acae;
              }
            }
          }
        }
        .btn-close {
          cursor: pointer;
          position: absolute;
          right: 0.5rem;
          top: 0.5rem;
          width: 1.25rem;
          height: 1.25rem;
          display: flex;
          justify-content: center;
          align-items: center;
          border-radius: 100%;
          opacity: 0.4;
          &:hover {
            opacity: 1;
          }
          .icon-close {
            width: 0.625rem;
            height: 0.625rem;
          }
        }
        .img-map {
          border-radius-top-left: ${settings.borderRadius}px;
          border-radius-bottom-left: ${settings.borderRadius}px;
          img {
            height: 100%;
            width: 100%;
          }
          height: 6rem;
          min-width: 6rem;
        }
        .notification-title {
          overflow: hidden;
          position: relative;
          left: -4px;
          padding-left: 4px;

          span {
            display: inline-block;
            margin-left: -4px;
            margin-bottom: 2px;
            padding: 2px 4px;
            line-height: 16px;
            font-size: 14px;
            font-weight: 500;
            color: #242328;
            text-overflow: ellipsis;
            overflow-x: hidden;
            max-width: 220px;
            vertical-align: middle;
          }
        }
        .notification-description {
          margin-top: 6px;
          margin-bottom: 5px;
          line-height: 13px;
          font-size: 13px;
          font-weight: 300;
          color: #545669;
          white-space: nowrap;
          overflow: hidden;
          .ui.label.error-mesage {
            position: absolute;
            top: 3.6rem;
          }
        }
        .from-date {
          display: inline-block;
          margin-right: 4px;
          line-height: 11px;
          font-size: 11px;
          font-weight: 300;
          color: #9196b6;
        }
      `}
    >
      {isEmptyImage ? (
        <div
          className='img-map overflow-hidden flex justify-center items-center'
          css={css`
            background-color: ${ewebinar.primaryColor ?? '#39A1B2'};
            color: ${readableColorOnBackground(ewebinar.primaryColor ?? '#39A1B2')};
          `}
        >
          <CheckIcon className='w-2e h-2e' />
        </div>
      ) : (
        <div className='img-map overflow-hidden'>
          <img src={conversion.imageUrl || ''} onError={(): void => setEmptyImage(true)} />
        </div>
      )}

      <div className='flex w-full pl-1e pr-2e py-3/4e'>
        <div className='flex w-full flex-col'>
          <div className='flex notification-title'>
            <span>{conversion.title}</span>
          </div>
          <div className='notification-description'>
            {isEditable ? (
              <Form.Field
                name='details.description'
                className='relative mb-0'
                component={
                  <Input
                    id='interactionDescription'
                    css={css`
                      input#interactionDescription {
                        color: #686b81;
                        border-color: #686b81;
                        padding: 2px 5px;
                        border-style: dashed;
                        border-radius: 4px !important;
                      }
                      & + .error-message {
                        position: absolute !important;
                        margin-top: 0.5rem !important;
                        top: 100%;
                      }
                    `}
                    placeholder='e.g. Signed up for a subscription'
                  />
                }
              />
            ) : (
              <span>
                {mode === PlayerMode.edit
                  ? descriptionInput || interaction?.details?.description || ''
                  : conversion.description}
              </span>
            )}
          </div>
          <FromNow
            dateString={conversion.fromDate}
            language={ewebinar.viewerRoomSettings.language ?? 'en'}
          />
        </div>
        <div
          className='btn-close'
          onClick={(e): void => {
            e.stopPropagation();
            e.preventDefault();
            onClose();
          }}
        >
          <CloseIcon className='icon-close' />
        </div>
      </div>
    </motion.div>
  );
});

const ConversionNotificationsSet: React.FC<{
  interaction: InteractionInfo;
  overlayMode: OverlayInteractionMode;
  isInSettings?: boolean;
}> = withWebinarPlayerContext(
  ['editMode', 'set', 'isPlaying'],
  'ConversionNotificationsSet'
)(({ interaction, overlayMode, context, isInSettings }) => {
  const { editMode, set, isPlaying } = context;

  const conversionsQuery = useConversionNotificationsQuery({
    variables: {
      interactionId: interaction.id ?? '',
    },
    fetchPolicy: editMode ? 'cache-first' : 'network-only',
    notifyOnNetworkStatusChange: true,
    skip: !interaction.id,
  });

  const [fakeConversionsQuery, fakeConversionsResult] = useFakeConversionNotificationsLazyQuery({
    variables: {
      setId: set.id,
      interactionId: interaction.id ?? '',
    },
  });

  const [disabled, setDisabled] = React.useState(
    () => !editMode && Cookies.get(`notification-disabled-${set.id}`) === 'true'
  );
  const [conversionsList, setConversionsList] = React.useState<ConversionNotificationFragment[]>(
    []
  );

  const durationRef = React.useRef<NodeJS.Timeout>();
  const [activeIndex, setActiveIndex] = React.useState(0);
  const [hidden, setHidden] = React.useState(false);

  React.useEffect(() => {
    if (conversionsQuery.loading) return;

    if (
      conversionsQuery.data?.conversionNotifications &&
      conversionsQuery.data?.conversionNotifications.length > 0
    ) {
      setConversionsList(conversionsQuery.data.conversionNotifications);
    } else if (editMode?.startsWith('edit') || editMode?.startsWith('dash')) {
      const fetchFakeConversions = async (): Promise<void> => {
        try {
          fakeConversionsQuery();
        } catch (e) {
          console.error(e);
        }
      };
      fetchFakeConversions();
    }
  }, [
    conversionsQuery.data?.conversionNotifications,
    conversionsQuery.loading,
    fakeConversionsQuery,
    editMode,
  ]);

  const fakeConversionNotifications = fakeConversionsResult.data?.fakeConversionNotifications;
  React.useEffect(() => {
    if (fakeConversionNotifications && fakeConversionNotifications.length > 0) {
      setConversionsList(fakeConversionNotifications ?? []);
    }
  }, [fakeConversionNotifications]);

  const haveConversionsChanged = useHasChangedShallow([conversionsList, activeIndex], {
    skipInitial: true,
  });
  React.useEffect(() => {
    if (overlayMode === OverlayInteractionMode.InCard) return;

    if (editMode && !isPlaying) {
      // Reset notification when stop preview
      setHidden(false);
      setActiveIndex(0);
      return;
    }

    // disabled or empty list of notifications
    if (disabled) return;

    if (activeIndex >= conversionsList.length) {
      setHidden(true);
      return;
    }

    // Show notifications
    logger.debug('ANIM', 'ConversionNotification showing ', activeIndex);
    setHidden(!Boolean(conversionsList[activeIndex]));
    durationRef.current && clearTimeout(durationRef.current);

    let delay: NodeJS.Timeout;
    durationRef.current = setTimeout(() => {
      logger.debug('ANIM', 'ConversionNotification hiding ', activeIndex);
      setHidden(true);

      delay && clearInterval(delay);
      delay = setInterval(() => {
        logger.debug('ANIM', 'ConversionNotification going to ', activeIndex + 1);
        setActiveIndex(activeIndex + 1);
      }, config.TIME_DELAY_BETWEEN_NOTIFICATIONS);
    }, config.TIME_TO_HIDDEN_CONVERSION_NOTIFICATIONS);

    return (): void => {
      logger.debug('ANIM', 'ConversionNotification Removing timers');

      delay && clearInterval(delay);
      durationRef.current && clearTimeout(durationRef.current);
    };
  }, [
    activeIndex,
    conversionsList,
    editMode,
    disabled,
    overlayMode,
    haveConversionsChanged,
    isPlaying,
  ]);

  const onMessage = React.useCallback(
    (notification: SocketNotification): void => {
      const newConversion = notification.newConversion;
      if (newConversion)
        setConversionsList((conversionsList) => {
          const first = conversionsList.slice(0, activeIndex + 1);
          const second = conversionsList.slice(activeIndex + 1);
          return first.concat(newConversion).concat(second);
        });
    },
    [activeIndex]
  );

  const onClose = React.useCallback((): void => {
    if (overlayMode === OverlayInteractionMode.InCard) return;

    setDisabled(true);
    if (!editMode) {
      Cookies.set(`notification-disabled-${set.id}`, 'true');
    }
  }, [editMode, overlayMode, set.id]);

  if (disabled || conversionsList.length === 0 || (isMobile && interaction.hideOnMobile())) {
    return null;
  }

  const indexToShow = overlayMode === OverlayInteractionMode.InCard ? 0 : activeIndex;

  return (
    <>
      {!editMode && <MessageEvent handler={onMessage} />}

      <AnimatePresence>
        {hidden ? null : (
          <div key={indexToShow}>
            <ConversionNotificationOverlayItem
              key={indexToShow}
              interaction={interaction}
              onClose={onClose}
              conversion={conversionsList[indexToShow]}
              mode={overlayMode}
              isInSettings={isInSettings}
            />
          </div>
        )}
      </AnimatePresence>
    </>
  );
});

export const ConversionNotificationsOverlay: React.FC<OverlayComponentProps> =
  withWebinarPlayerContext(
    ['mode'],
    'ConversionNotificationsOverlay'
  )(({ interaction, overlayMode, isInSettings, context: { mode } }) => {
    const notification = React.useMemo(
      () => (
        <ConversionNotificationsSet
          interaction={interaction}
          overlayMode={overlayMode}
          isInSettings={isInSettings}
        />
      ),
      [interaction, overlayMode, isInSettings]
    );

    if (mode !== PlayerMode.edit || overlayMode === OverlayInteractionMode.InCard) {
      return <>{notification}</>;
    }

    return (
      <div
        id={`ConversionNotificationSet-${interaction.shortId}`}
        className={`
        z-player-control display-${interaction.displayLocation()}
      `}
        css={css`
          pointer-events: none;

          position: absolute;
          left: 50%;

          &.display-top {
            top: 2%;
            transform-origin: top left;
          }

          &.display-bottom {
            bottom: 2%;
            transform-origin: bottom left;
          }
        `}
      >
        <div
          css={css`
            transform: translateX(-50%);
          `}
        >
          {notification}
        </div>
      </div>
    );
  });
